import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { reject, map, groupBy, compose, values, filter } from 'ramda';
import { Controller, useForm, useFieldArray } from 'react-hook-form';

import { Row, Column } from 'components/shared/Layout';
import Checkbox from 'components/ui/Checkbox';
import Input from 'components/ui/Input';
import { multiply } from 'utils/calculator';
import currencySymbols from 'constants/currencies';
import { bookingShape, legShape, productsShape } from 'constants/propTypes';
import productTypes from '../../../../constants/productTypes';
import {
  ButtonContainer,
  StyledLabel,
  StyledHeaderLabel,
  ToggleWrapper,
  Toggle,
  ActionHeader,
  ActionContent,
  FieldError,
  StyledFormLabel,
  StyledConfirmButton,
  StyledLinkButton
} from './ExtrasTrain.styles';
import { ADD_ADDITIONAL_DETAILS, UPDATE_ADDITIONAL_DETAILS } from 'store/mutations';
import { useMutation } from '@apollo/client';

const ExtrasJourneyProduct = ({ disabled, direction, extras, onCheckboxChange, itemsToCancel, booking, refetch }) => {
  const [addAdditionalDetails, { loading: isAddLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);
  const [updateAdditionalDetails, { loading: isUpdateLoading }] = useMutation(UPDATE_ADDITIONAL_DETAILS);

  const combineDuplicateProductsWithQuantity = (p) => ({
    product: {
      ...p[0],
      itemIds: p.filter((product) => !product.cancelled).map((product) => product.itemId),
      itemRefs: p.filter((product) => !product.cancelled).map((product) => product.itemRef),
      additionalDetails: p
        .filter((product) => !product.cancelled)
        .map((product) => product.additionalDetails)
        .flat()
    },
    quantity: p.filter((product) => !product.cancelled).length
  });

  const products = extras || direction.extraProducts;

  const getExtraUniqKey = (extra) => `${extra.code}-${extra.price}-${extra.passengerId}-${extra.currency}`;

  const extraProductsByPassengers = compose(
    map((p) => ({ product: p.product, quantity: p.quantity })),
    map(combineDuplicateProductsWithQuantity),
    values(),
    groupBy((p) => getExtraUniqKey(p)),
    filter((i) => !i.cancelled),
    reject((i) => i.type === productTypes.DISABILITY_SUPPORT)
  )(products);

  const [extrasCountMap, setExtrasCountMap] = useState(
    extraProductsByPassengers.reduce((acc, extra) => {
      acc[getExtraUniqKey(extra.product)] = extra.quantity;

      return acc;
    }, {})
  );

  const [extrasToggleMap, setExtrasToggleMap] = useState(
    extraProductsByPassengers.reduce((acc, extra) => {
      acc[getExtraUniqKey(extra.product)] = true;

      return acc;
    }, {})
  );

  const [extrasShouldTransition, setExtrasShouldTransition] = useState(
    extraProductsByPassengers.reduce((acc, extra) => {
      acc[getExtraUniqKey(extra.product)] = false;

      return acc;
    }, {})
  );

  const [extrasHeight, setExtrasHeight] = useState(
    extraProductsByPassengers.reduce((acc, extra) => {
      acc[getExtraUniqKey(extra.product)] = 0;

      return acc;
    }, {})
  );

  const contentElMap = extraProductsByPassengers.reduce((acc, extra) => {
    acc[getExtraUniqKey(extra.product)] = React.createRef();
    return acc;
  }, {});

  useEffect(() => {
    for (const uniqKey in contentElMap) {
      if (!contentElMap[uniqKey].current || extrasHeight[uniqKey] !== 0) {
        return;
      }

      const computedHeight = window.getComputedStyle(contentElMap[uniqKey].current).height;

      setExtrasHeight({
        ...extrasHeight,
        [uniqKey]: computedHeight
      });

      setExtrasToggleMap({
        ...extrasToggleMap,
        [uniqKey]: false
      });

      setExtrasShouldTransition({
        ...extrasShouldTransition,
        [uniqKey]: true
      });
    }
  }, [contentElMap, extrasHeight, extrasShouldTransition, extrasToggleMap]);

  const handleExtrasCountChange = (e, itemRefs, uniqKey) => {
    const count = Number(e.target.value || 0);
    if (count < 1 || count > itemRefs.length) {
      return;
    }
    setExtrasCountMap({
      ...extrasCountMap,
      [uniqKey]: count
    });

    onCheckboxChange(itemRefs, count);
  };

  const handleExtrasCheckBoxChange = (itemRefs, uniqKey) => {
    setExtrasCountMap({
      ...extrasCountMap,
      [uniqKey]: itemRefs.length
    });

    onCheckboxChange(itemRefs);
  };

  const toggle = (uniqKey) => {
    setExtrasToggleMap({
      ...extrasToggleMap,
      [uniqKey]: !extrasToggleMap[uniqKey]
    });
  };

  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors }
  } = useForm({
    defaultValues: {
      rows: extraProductsByPassengers
        .map((p) =>
          p.product.itemIds.map((i) => {
            let value = booking?.additionalDetails?.find((detail) => detail.key === 'luggage_details' && detail.itemRef === i)?.value;
            let itemRef = products.find((product) => product.itemId === i)?.itemRef;

            let coachNumber = '';
            let trainSeal = '';
            let itemSeal = '';

            if (value) {
              const details = value.split('$');
              if (details.length === 3) {
                coachNumber = details[0];
                trainSeal = details[1];
                itemSeal = details[2];
              }
            }

            return {
              id: itemRef,
              name: itemRef,
              coachNumber: coachNumber,
              trainSeal: trainSeal,
              itemSeal: itemSeal
            };
          })
        )
        .flat()
    }
  });

  const { fields } = useFieldArray({
    name: 'rows',
    control
  });

  const areDetailsComplete = (product) =>
    product.itemIds.every((i) =>
      booking?.additionalDetails?.some(
        (detail) => detail.key === 'luggage_details' && detail.itemRef === i && detail.value.split('$').length === 3
      )
    );

  const onApplyToAll = (itemRefs) => {
    let firstItemRef = itemRefs[0];
    const index = fields.findIndex((field) => field.name === firstItemRef);
    const data = getValues(`rows.${index}`);

    for (const itemRef of itemRefs) {
      if (itemRef === firstItemRef) {
        continue;
      }

      const rowIndex = fields.findIndex((field) => field.name === itemRef);
      setValue(`rows.${rowIndex}.coachNumber`, data.coachNumber);
      setValue(`rows.${rowIndex}.trainSeal`, data.trainSeal);
    }
  };

  const onSaveRow = async (itemRefs) => {
    const adding = [];
    const updating = [];

    for (const itemRef of itemRefs) {
      const index = fields.findIndex((field) => field.name === itemRef);

      // trigger just this row’s three fields
      const valid = await trigger([`rows.${index}.coachNumber`, `rows.${index}.trainSeal`, `rows.${index}.itemSeal`]);

      if (!valid) {
        // errors object will now be populated *only* for those fields
        console.warn('Row validation failed:', errors.rows?.[index]);
        return;
      }

      const rowData = getValues(`rows.${index}`);

      const entry = {
        itemRefId: parseInt(itemRef, 10),
        key: 'luggage_details',
        value: `${rowData.coachNumber}$${rowData.trainSeal}$${rowData.itemSeal}`
      };

      if (booking?.additionalDetails?.some((detail) => detail.itemRefId === entry.itemRefId && detail.key === entry.key)) {
        updating.push(entry);
      } else {
        adding.push(entry);
      }
    }

    if (updating.length > 0) {
      updateAdditionalDetails({
        variables: {
          bookingReference: booking.reference,
          additionalDetails: updating
        }
      }).then(() => {
        refetch({ reference: booking.reference });
      });
    }

    if (adding.length > 0) {
      addAdditionalDetails({
        variables: {
          bookingReference: booking.reference,
          additionalDetails: adding
        }
      }).then(() => {
        refetch({ reference: booking.reference });
      });
    }
  };

  const loading = isAddLoading || isUpdateLoading;

  return (
    <div data-testid="extra-journey-product" data-cy="extra-journey-product">
      {extraProductsByPassengers.length > 0 ? (
        extraProductsByPassengers.map(({ product, quantity }) => {
          const { itemRefs, passenger, name, price, currency } = product;
          const uniqKey = getExtraUniqKey(product);

          const hasChecked = !!itemsToCancel.find((i) => itemRefs.includes(i));
          const shouldDisplayCountInput = hasChecked && itemRefs.length > 1;
          const isLuggage = product.type === productTypes.LUGGAGE;

          return (
            <div key={uniqKey} data-cy="extra-product" data-testid="extra-product">
              <Row alignItems="center" marginTop={hasChecked ? '30px' : '0px'}>
                <Column span={5}>
                  {passenger.firstName} {passenger.lastName}
                </Column>
                <Column span={5}>
                  {quantity} {name}
                </Column>
                <Column span={2}>{`${currencySymbols[currency]}${multiply(quantity, price)}`}</Column>
                <Column data-cy="extra-product-checkbox" span={1}>
                  {!disabled && (
                    <Checkbox disabled={disabled} checked={hasChecked} onChange={() => handleExtrasCheckBoxChange(itemRefs, uniqKey)} />
                  )}{' '}
                </Column>
                {shouldDisplayCountInput && (
                  <Column data-cy="extra-product-count-input" span={3} style={{ position: 'relative' }}>
                    {itemsToCancel.find((i) => itemRefs.includes(i)) && itemRefs.length > 1 && (
                      <>
                        <StyledLabel htmlFor={`extra-product-count-input-${uniqKey}`}>
                          <FormattedMessage id="extras.refundQuantity" />
                        </StyledLabel>
                        <Input
                          type="number"
                          min="1"
                          max={itemRefs.length}
                          data-testid={`extra-product-count-input-${uniqKey}`}
                          id={`extra-product-count-input-${uniqKey}`}
                          name={`extra-product-count-input-${uniqKey}`}
                          value={extrasCountMap[uniqKey]}
                          onChange={(event) => handleExtrasCountChange(event, itemRefs, uniqKey)}
                        />
                      </>
                    )}
                  </Column>
                )}
                {!shouldDisplayCountInput && isLuggage && (
                  <Column data-cy="extra-product-seal" span={3}>
                    <ActionHeader onClick={() => toggle(uniqKey)} isComplete={areDetailsComplete(product)}>
                      <StyledHeaderLabel>
                        <FormattedMessage id={areDetailsComplete(product) ? 'luggage.completed' : 'luggage.required'} />
                      </StyledHeaderLabel>

                      <ToggleWrapper>
                        <Toggle isExpanded={extrasToggleMap[uniqKey]} />
                      </ToggleWrapper>
                    </ActionHeader>
                  </Column>
                )}
              </Row>

              <ActionContent
                isExpanded={extrasToggleMap[uniqKey]}
                ref={contentElMap[uniqKey]}
                shouldTransition={extrasShouldTransition[uniqKey]}
                height={extrasHeight[uniqKey]}
              >
                {itemRefs.map((itemRef) => {
                  const index = fields.findIndex((field) => field.name === itemRef);

                  return (
                    <Row key={itemRef} alignItems="center" marginTop="10px">
                      <Column span={2}>
                        <StyledFormLabel htmlFor={`extra-product-coach-number-${itemRef}`}>
                          <FormattedMessage id="luggage.coach" />
                        </StyledFormLabel>
                      </Column>
                      <Column span={2}>
                        <Controller
                          control={control}
                          name={`rows.${index}.coachNumber`}
                          rules={{
                            required: 'luggage.required',
                            validate: (v) => !v.includes('$') || 'luggage.dollar'
                          }}
                          render={({ field, fieldState }) => (
                            <>
                              <Input {...field} type="text" id={`extra-product-coach-number-${itemRef}`} />
                              {fieldState.error && (
                                <FieldError>
                                  <FormattedMessage id={fieldState.error.message} />
                                </FieldError>
                              )}
                            </>
                          )}
                        />
                      </Column>

                      <Column span={2}>
                        <StyledFormLabel htmlFor={`extra-product-train-seal-${itemRef}`}>
                          <FormattedMessage id="luggage.trainSeal" />
                        </StyledFormLabel>
                      </Column>
                      <Column span={4}>
                        <Controller
                          control={control}
                          name={`rows.${index}.trainSeal`}
                          rules={{
                            required: 'luggage.required',
                            validate: (v) => !v.includes('$') || 'luggage.dollar'
                          }}
                          render={({ field, fieldState }) => (
                            <>
                              <Input {...field} type="text" id={`extra-product-train-seal-${itemRef}`} />
                              {fieldState.error && (
                                <FieldError>
                                  <FormattedMessage id={fieldState.error.message} />
                                </FieldError>
                              )}
                            </>
                          )}
                        />
                      </Column>

                      <Column span={2}>
                        <StyledFormLabel htmlFor={`extra-product-item-seal-${itemRef}`}>
                          <FormattedMessage id="luggage.itemSeal" />
                        </StyledFormLabel>
                      </Column>
                      <Column span={4}>
                        <Controller
                          control={control}
                          name={`rows.${index}.itemSeal`}
                          rules={{
                            required: 'luggage.required',
                            validate: (v) => !v.includes('$') || 'luggage.dollar'
                          }}
                          render={({ field, fieldState }) => (
                            <>
                              <Input {...field} type="text" id={`extra-product-item-seal-${itemRef}`} />
                              {fieldState.error && (
                                <FieldError>
                                  <FormattedMessage id={fieldState.error.message} />
                                </FieldError>
                              )}
                            </>
                          )}
                        />
                      </Column>
                    </Row>
                  );
                })}

                <ButtonContainer>
                  <StyledLinkButton
                    loading={loading}
                    disabled={loading}
                    type="button"
                    onClick={() => {
                      onApplyToAll(itemRefs);
                    }}
                  >
                    <FormattedMessage id="luggage.apply" />
                  </StyledLinkButton>

                  <StyledConfirmButton
                    loading={loading}
                    disabled={loading}
                    type="button"
                    onClick={() => {
                      onSaveRow(itemRefs);
                    }}
                  >
                    <FormattedMessage id="luggage.save" />
                  </StyledConfirmButton>
                </ButtonContainer>
              </ActionContent>

              <hr />
            </div>
          );
        })
      ) : (
        <FormattedMessage id="extras.noProducts" />
      )}
    </div>
  );
};

ExtrasJourneyProduct.propTypes = {
  disabled: PropTypes.bool,
  direction: legShape,
  extras: productsShape,
  onCheckboxChange: PropTypes.func,
  itemsToCancel: PropTypes.arrayOf(PropTypes.string),
  booking: bookingShape,
  refetch: PropTypes.func.isRequired
};

ExtrasJourneyProduct.defaultProps = {
  itemsToCancel: []
};

export default ExtrasJourneyProduct;
